import * as R from 'ramda'
import { runSubqlQuery } from '@/utils/requests'

//
// Types & Interfaces
//

interface MintedOriginOfShellNft {
  id: string
  createdAt: string
  rarity: string
  career: string
  race: string
}

interface OriginOfShellPreorder {
  id: string
  createdAt: string
  owner: string
  preorderId: number
  nftId: number | null
  status: 'PENDING' | 'CHOSEN' | 'REFUNDED'
  career: string
  race: string
}

interface PreorderListQueryResult {
  data: {
    mintedOriginOfShellNfts: {
      nodes: MintedOriginOfShellNft[]
    }
    originOfShellPreorders: {
      nodes: OriginOfShellPreorder[]
    }
  }
}

export interface Preorder {
  id: number
  owner: string
  status: 'PENDING' | 'CHOSEN' | 'REFUNDED'
  nft?: {
    id: number
    rarity: string
    career: string
    race: string
    createdAt: string
  }
  career: string
  race: string
  createdAt: string
}

interface PreorderCountsByRaceGroupedAggregates {
  distinctCount: {
    id: string
  }
  keys: [string]
}

interface PreorderCountsByRaceResult {
  data: {
    originOfShellPreorders: {
      groupedAggregates: PreorderCountsByRaceGroupedAggregates[]
    }
  }
}

type GetPurchaseCodeResponse = {
  status: 'ok'
  purchase_code: {
    message: string;
    signature: string;
  }
} | {
  status: 'error'
  error: string
}

//
// Functions
//

export async function getPreorderList(endpoint: string, owner: string) {
  const data = await runSubqlQuery<PreorderListQueryResult>(
    endpoint,
    `
      query ($owner: String!) {
        mintedOriginOfShellNfts (
          filter:{owner: {equalTo: $owner}}
          orderBy: CREATED_AT_DESC
        ) {
          nodes {
              id,
              createdAt,
              rarity,
              career,
              race
          }
        }
        originOfShellPreorders (
          filter:{owner: {equalTo: $owner}}
          orderBy: CREATED_AT_DESC
        ) {
          nodes {
            id,
            createdAt,
            owner,
            preorderId,
            nftId,
            career,
            race,
            status
          }
        }
      }
    `,
    {owner}
  )
  const nfts = R.fromPairs(
    R.map(
      (nft: MintedOriginOfShellNft) => {
        const nftId = parseInt(R.last(nft.id.split('-')) as string, 10)
        return [
          `${nftId}`,
          {
            id: nftId,
            createdAt: nft.createdAt,
            rarity: nft.rarity,
            career: nft.career,
            race: nft.race,
          },
        ]
      },
      R.pathOr([], ['data', 'mintedOriginOfShellNfts', 'nodes'], data)
    ),
  )
  const result: Preorder[] = R.map(
    (preorder: OriginOfShellPreorder) => ({
      id: preorder.preorderId,
      owner: preorder.owner,
      status: preorder.status,
      race: preorder.race,
      career: preorder.career,
      nft: preorder.nftId ? nfts[`${preorder.nftId}`] : undefined,
      createdAt: preorder.createdAt,
    }),
    R.pathOr([], ['data', 'originOfShellPreorders', 'nodes'], data)
  )
  return result
}

export async function countOwnerPreorder(endpoint: string, owner: string) {
  const data = await runSubqlQuery<{
    data: {
      originOfShellPreorders: {
        aggregates: {distinctCount: {preorderId: string}}
      }
    }
  }>(
    endpoint,
    `
      query ($owner: String!) {
        originOfShellPreorders (
          filter: {owner: {equalTo: $owner}}
        ) {
          aggregates {
            distinctCount {
              preorderId
            }
          }
        }
      }
    `,
    {owner}
  )
  const counts = R.pathOr(
    '0',
    [
      'data',
      'originOfShellPreorders',
      'aggregates',
      'distinctCount',
      'preorderId',
    ],
    data
  )
  return parseInt(counts, 10)
}

export async function reportReferrer(endpoint: string, address: string, ref_code?: string) {
  if (ref_code) {
    await fetch(`${endpoint}/referrals`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ address, ref_code }),
    })
  }
}

const transformPreorderCounts = R.pipe(
  R.pathOr([], ['data', 'originOfShellPreorders', 'groupedAggregates']),
  R.map(i => [R.path(['keys', 0], i), parseInt(R.pathOr('0', ['distinctCount', 'id'], i), 10)] as Pairs<string, number>),
  R.fromPairs,
  R.mergeRight({ AISpectre: 0, Pandroid: 0, XGene: 0, Cyborg: 0 }),
)

export async function getPreorderCounts(endpoint: string) {
  const result = await runSubqlQuery<PreorderCountsByRaceResult>(endpoint, `
    query {
      originOfShellPreorders(filter: {status: {equalTo: PENDING}}) {
        groupedAggregates (groupBy: [RACE]) {
          keys
          distinctCount {
            id
          }
        }
      }
    }
  `)
  return transformPreorderCounts(result)
}

export async function getPurchaseCode(prefix: string, address: string): Promise<ResponsePairs<string>> {
  try {
    const resp = await fetch(`${prefix}/spirits/${address}/purchase_code`, { method: 'POST' })
    const data: GetPurchaseCodeResponse = await resp.json()
    if (data && data.status === 'ok') {
      return [true, data.purchase_code.signature]
    }
    return [false, `${data.error}`];
  } catch (err) {
    // console.error(err)
    return [false, `${err}`];
  }
}